import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useRouteError } from 'react-router-dom';
import { getEnvConfig } from '@configs/env';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { Button } from '@mui/material';
import { ErrorIcon } from '@icons/ErrorIcon';

export const RouterErrorComponent = ({ text, errornum }: { text?: string; errornum?: number }) => {
  console.log('🚀 ~ RouterErrorComponent ~ text:', text);
  const routerError = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { ONLY_USE_LOGIN_PAGE } = getEnvConfig();
  let enabledOnlyUseLoginPage = ONLY_USE_LOGIN_PAGE === 'true';
  let routerErrorNum = errornum;
  let routerErrorText = text;

  // status에 따른 버튼 텍스트와 동작 결정
  let buttonText = t('Error.Button_Go_Back');
  let buttonAction = () => navigate(-1);

  if (routerError instanceof Response) {
    routerErrorNum = routerError.status;

    // status 코드별 메시지 처리
    switch (routerError.status) {
      case 401:
        routerErrorText = t('Error.Auth_Required');
        routerErrorNum = 401;
        break;
      /**
       * INVALID_TENANT: 접근할 수 있는 테넌트가 아닙니다.
       * INVALID_USER: 관리자 권한이 없습니다.
       */
      case 403:
        if (routerError.statusText === 'INVALID_TENANT') {
          routerErrorText = t('Error.Invalid_Tenant');
          routerErrorNum = 403;
        } else if (routerError.statusText === 'INVALID_USER') {
          routerErrorText = t('Error.Invalid_User');
          routerErrorNum = 403;
        } else {
          routerErrorText = t('Error.No_Permission');
          routerErrorNum = 403;
        }
        break;
      case 500:
        routerErrorText = t('Error.Server_Error');
        routerErrorNum = 500;
        break;
      default:
        routerErrorText = routerError.statusText || t('Error.Unknown_Error');
        routerErrorNum = 500;
    }
  } else if (routerError instanceof Error) {
    routerErrorNum = 500;
    routerErrorText = routerError.message || t('Error.Server_Error');
  } else if (location.state?.errorNum && location.state?.errorText) {
    switch (location.state.errorNum) {
      case 403:
        if (location.state.errorText === 'NO_ACCESSIBLE_TENANT') {
          routerErrorText = t('Error.No_Accessible_Tenant');
          routerErrorNum = 403;
          enabledOnlyUseLoginPage = true; // 무조건 로그인 화면으로
        } else {
          routerErrorText = t('Error.No_Permission');
          routerErrorNum = 403;
        }
        break;
      default:
        routerErrorText = location.state.errorText || t('Error.Unknown_Error');
        routerErrorNum = 500;
    }
  }

  if (!routerErrorNum) {
    routerErrorNum = 500;
    routerErrorText = t('Error.Server_Error');
  }

  /**
   * @description managerRouter에 정의된 페이지에서 에러가 발생할 경우
   * navigate를 사용하면 managerRouter에 정의된 스토어로 이동되기 때문에
   * employeeRouter에 정의된 스토어로 이동시키기 위해 window.location.href를 사용합니다.
   */
  if (routerErrorNum === 401) {
    buttonText = t('Error.Button_Login');
    buttonAction = () => {
      window.location.href = '/login';
    };
  } else if (routerErrorNum === 403) {
    buttonText = enabledOnlyUseLoginPage ? t('Error.Button_Login') : t('Error.Button_Go_Store');
    buttonAction = () => {
      window.location.href = enabledOnlyUseLoginPage ? '/login' : '/store';
    };
  }

  return (
    <ErrorContainer>
      <ErrorCard>
        <IconWrapper>
          <ErrorIcon width={120} height={120} />
        </IconWrapper>
        {/* <ErrorCode>{routerErrorNum}</ErrorCode> */}
        {/* <ErrorTitle>앗! 문제가 발생했습니다</ErrorTitle> */}
        <ErrorMessage>{routerErrorText}</ErrorMessage>
        <StyledButton variant="contained" size="large" color="primary" onClick={buttonAction}>
          {buttonText}
        </StyledButton>
      </ErrorCard>
    </ErrorContainer>
  );
};
export const ErrorComponent = ({ text, errornum, infotxt }: { text: string; errornum: number; infotxt?: string }) => {
  const { t } = useTranslation();

  return (
    <ErrorContainer>
      <ErrorCard>
        <IconWrapper>
          <ErrorIcon width={120} height={120} />
        </IconWrapper>
        {/* {errornum !== 404 && <ErrorCode>{errornum}</ErrorCode>} */}
        <ErrorTitle>{errornum === 404 ? t('Error.No_Search_Result') : text}</ErrorTitle>
        {infotxt && <ErrorMessage>{infotxt}</ErrorMessage>}
      </ErrorCard>
    </ErrorContainer>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 20px;
`;

const ErrorCard = styled.div`
  background: white;
  border-radius: 24px;
  padding: 48px 48px 40px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08), 0 0 1px rgba(0, 0, 0, 0.1);
  text-align: center;
  animation: ${fadeIn} 0.6s ease-out;
`;

const IconWrapper = styled.div`
  animation: ${float} 3s ease-in-out infinite;

  svg {
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1));
  }
`;

// const ErrorCode = styled.h1`
//   font-size: 72px;
//   font-weight: 800;
//   color: #4f46e5;
//   margin: 0;
//   margin-bottom: 12px;
//   line-height: 1.1;
//   /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
//   background: ${({ theme }) => theme.colors['bg-gradient']};
//   -webkit-background-clip: text;
//   -webkit-text-fill-color: transparent;
//   background-clip: text;
//   letter-spacing: -2px;
//   display: block;
//   padding: 4px 8px;
// `;

const ErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
  margin-bottom: 8px;
  line-height: 1.4;
`;

const ErrorMessage = styled.p`
  font-size: 24px;
  font-weight: 700;
  color: #6b7280;
  margin: 0;
  margin-bottom: 32px;
  line-height: 1.6;
  white-space: pre-line;
`;

const StyledButton = styled(Button)`
  padding: 14px 32px;
  font-size: 16px;
  font-weight: 600;
  text-transform: none;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: none;
  }
`;
